
import { FormSubSections } from '../entities/formTemplateDataType';
import { ElementEntity } from '../entities/ElementEntity';
import { CXPAddress } from '../runtimeComponent/subComponent/AddressComponent';
import { LotTypeMap, PropertIdentifier, PropertIdentifierMap } from '../entities/Enums';
import { Address } from '../runtimeComponent/subComponent/AddressSearchComponent';
import DataUtils from '../utilities/DataUtils';
import { getRootComponent } from './ElementUtils';
import { parseJson, stringifyJson } from './JsonUtils';
import { parseJSON } from 'date-fns';

export default class Utils {
    static IsThereTableLevel(wholeNode: Node): boolean {
        var xpath = 'Section/Section[Type=\'Table\']';
        return !this.IsThereNoLevelNode(wholeNode, xpath);
    }

    static IsThereNoLevelNode(wholeNode: Node, xpath: string): boolean {
        var xPathResult: XPathResult = this.getNode(wholeNode, xpath);
        var node: Node = xPathResult.iterateNext();
        return !node;
    }

    static getNode(node: Node, xpath: string): XPathResult {
        var evaluator = new XPathEvaluator();
        var resolver = evaluator.createNSResolver(node);
        var results = evaluator.evaluate(xpath, node, resolver, XPathResult.ANY_TYPE, null);
        return results;
    }

    static getOneNode(node: Node, xpath: string): string {
        var evaluator = new XPathEvaluator();
        var resolver = evaluator.createNSResolver(node);
        var results = evaluator.evaluate(xpath, node, resolver, XPathResult.ANY_TYPE, null);
        var firstNode: Node = results.iterateNext();
        if (firstNode)
            return firstNode.textContent;
        else
            return null;
    }

    static readOnlyAccess(fieldNode: Node) {
        var fieldElement: Element = <Element>fieldNode;
        var element = fieldElement.attributes.getNamedItem('access');
        if (element) {
            return element.value === 'readOnly';
        } else {
            return false;
        }
    }

    static IsMandatory(fieldNode: Node) {
        var fieldElement: Element = <Element>fieldNode;
        var element = fieldElement.attributes.getNamedItem('mandatory');
        if (element) {
            return element.value === '1';
        } else {
            return false;
        }
    }

    static getTitle(fieldNode: Node): string {
        var fieldNode = this.getField(fieldNode, 'Title', 'en').iterateNext();
        if (fieldNode) {
            return fieldNode.textContent;
        } else {
            return '';
        }
    }

    static getField(node: Node, fieldName: string, lang: string = 'en') {
        var xpathField: string = `${fieldName}[@xml:lang=\'${lang}\']`;
        return this.getNode(node, xpathField);
    }

    static IsThereFieldNextLevel(node: Node) {
        var xpathField: string = 'Field';
        return this.getNode(node, xpathField).iterateNext();
    }

    static GetNextLevelUntilHasField(node: Node) {
        var nextLevelPathResult: XPathResult = this.getNode(node, 'Section');
        var nextLevelNode: Node = nextLevelPathResult.iterateNext();
        if (this.IsThereFieldNextLevel(nextLevelNode)) {
            return this.getNode(node, 'Section');
        } else {
            return this.GetNextLevelUntilHasField(nextLevelNode);
        }
    }

    static getValueList(fieldNode: Node) {
        var list = [];
        var items: XPathResult = this.getNode(fieldNode, 'ListOfLOVValues/ENU');
        var item = items.iterateNext();
        while (item) {
            list.push({ label: item.textContent, value: item.textContent });
            item = items.iterateNext();
        }

        return list;
    }

    static getH2Node(wholeNode: Node) {
        var xpath = 'descendant::Section[@role=\'H2\']';
        return this.getNode(wholeNode, xpath);
    }
    static IsThereH2Node(wholeNode: Node) {
        var xpath = 'descendant::Section[@role=\'H2\']';
        return this.getNode(wholeNode, xpath).iterateNext();
    }
    static getAllFields(detailNode: Node) {
        var xpath: string = 'descendant::Field[not(@presence=\'hidden\')]';
        var evaluator = new XPathEvaluator();
        var resolver = evaluator.createNSResolver(detailNode);
        var results = evaluator.evaluate(xpath, detailNode, resolver, XPathResult.ANY_TYPE, null);
        return results;
    }

    static getAllTables(detailNode: Node) {
        var xpath: string = 'descendant::Type[@presence=\'hidden\')]';
        var evaluator = new XPathEvaluator();
        var resolver = evaluator.createNSResolver(detailNode);
        var results = evaluator.evaluate(xpath, detailNode, resolver, XPathResult.ANY_TYPE, null);
        return results;
    }

    static getH2NodeArray(wholeNode: Node) {
        var h2NodeArray = new Array();
        var h2Resultset = this.getH2Node(wholeNode);
        var h2Node = h2Resultset.iterateNext();
        while (h2Node) {
            h2NodeArray.push(h2Node);
            h2Node = h2Resultset.iterateNext();
        }

        return h2NodeArray;
    }

    static getH2TitleArray(wholeNode: Node) {
        var h2NodeTitleArray = new Array();
        var h2Resultset = this.getH2Node(wholeNode);
        var h2Node = h2Resultset.iterateNext();
        while (h2Node) {
            const title = Utils.getTitle(h2Node);
            h2NodeTitleArray.push(title);
            h2Node = h2Resultset.iterateNext();
        }

        return h2NodeTitleArray;
    }

    static getH2FieldArray(wholeNode: Node) {
        var h2FieldArray = new Array();
        var h2NodeArray = this.getH2NodeArray(wholeNode);

        h2NodeArray.forEach(h2Node => {
            var fieldResult = this.getAllFields(h2Node);
            var fieldNode = fieldResult.iterateNext();

            while (fieldNode) {
                h2FieldArray.push(fieldNode);
                fieldNode = fieldResult.iterateNext();
            }
        });

        return h2FieldArray;
    }

    static getArray(node: Node, xpath: string): Node[] {
        var evaluator = new XPathEvaluator();
        var resolver = evaluator.createNSResolver(node);
        var xPathresults = evaluator.evaluate(xpath, node, resolver, XPathResult.ANY_TYPE, null);
        var results = [];
        var node: Node = xPathresults.iterateNext();

        while (node) {
            results.push(node);
            node = xPathresults.iterateNext();
        }

        return results;
    }

    static compare(a, b): number {
        if (a instanceof Node && b instanceof Node) {
            return (parseFloat(Utils.getOneNode(a, 'Sequence')) - parseFloat(Utils.getOneNode(b, 'Sequence')));
        }
        return (parseFloat(a.Sequence) - parseFloat(b.Sequence));
    }

    static containBracket(title: string): boolean {
        let found: boolean = (title.indexOf('(') > 0 && title.indexOf(')') > 0);
        return found;
    }

    static stripLineBreaks(str: string): string {
        if (!str) return null;
        let index;
        let ret = str;
        while ((index = ret.indexOf('\\br')) !== -1) {
            ret = ret.substr(0, index) + ret.substr(index + 3);
        }
        while ((index = ret.indexOf('\\n')) !== -1) {
            ret = ret.substr(0, index) + ret.substr(index + 2);
        }
        return ret;
    }

    static extractSubString(title: string): string[] {
        var splitString = [];
        let startingPosition: number = title.indexOf('(');
        let endPosition: number = title.indexOf(')');
        splitString.push(title.substr(0, startingPosition));
        splitString.push(title.substr(startingPosition));
        return splitString;
    }

    static isArrayNotEmpty(array: any[]): boolean {
        if (!Array.isArray(array) || !array.length) {
            return false;
        }
        return true;
    }

    static generateRandomNumber(): number {
        return Math.floor(Math.random() * 1000);
    }

    static cloneSubSection(sourceSection: FormSubSections): FormSubSections {
        let theCopy: FormSubSections = null;
        const root = getRootComponent(sourceSection);
        theCopy = parseJson(stringifyJson(sourceSection, root.CategoryId), root.CategoryId);
        return theCopy;
    }

    static getFormattedDate(date) {
        var year = date.getFullYear();

        var month = (1 + date.getMonth()).toString();
        month = month.length > 1 ? month : '0' + month;

        var day = date.getDate().toString();
        day = day.length > 1 ? day : '0' + day;

        return month + '/' + day + '/' + year;
    }

    static longestStrInArray(arra) {
        let max_str = arra[0].length;
        let ans = arra[0];
        for (let i = 1; i < arra.length; i++) {
            const maxi = arra[i].length;
            if (maxi > max_str) {
                ans = arra[i];
                max_str = maxi;
            }
        }
        return ans;
    }

    static executeFunctionByName = (functionName, context) => {
        var namespaces = functionName.split(".");
        var func = namespaces.pop();
        for (var i = 0; i < namespaces.length; i++) {
            context = context[namespaces[i]];
        }
        return context[func].apply(context, null);
    }
    static isNotNullOrEmpty(value) {
        // Check for null or undefined
        if (value === null || value === undefined) {
            return false;
        }

        // Check for an empty string
        if (typeof value === 'string' && value.trim() === '') {
            return false;
        }

        // Check for an empty array
        if (Array.isArray(value) && value.length === 0) {
            return false;
        }

        // For other cases, consider the value as not null, not empty, and not undefined
        return true;
    }

    static isNullOrEmpty(value) {
        // Check for null or undefined
        if (value === null || value === undefined) {
            return true;
        }

        // Check for an empty string
        if (typeof value === 'string' && value.trim() === '') {
            return true;
        }

        // Check for an empty array
        if (Array.isArray(value) && value.length === 0) {
            return true;
        }

        // For other cases, consider the value as not null, not empty, and not undefined
        return false;
    }

    static isEmpty(obj) {
        for (let key in obj) {
            if (obj.hasOwnProperty(key)) {
                return false;
            }
        }
        return true; 
    }
    static extractPostalCode = (address) => {
        // Define a regular expression pattern to match Canadian postal codes
        const postalCodeRegex = /[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d/;
    
        // Find the postal code in the address using the regular expression
        const match = address.match(postalCodeRegex);
    
        // Return the matched postal code or null if not found
        return match ? match[0] : null;
      }

      static extractAddressNumber(address='') {
        // Regular expression pattern to match the address number
        var pattern = /^\d+/; // Match one or more digits at the beginning of the string
        
        // Use regex to find the address number
        var match = address.match(pattern);
        
        if (match) {
            return match[0]; // Return the matched address number
        } else {
            return null; // Return null if no match found
        }
    }

    static extractStreetName(address='') {
        // Regular expression to match the street name
        var regex = /(\d+ )?(.+)/; // Matches any number of digits followed by any characters
    
        // Extract the street name using the regular expression
        var match = address.match(regex);
    
        // Return the matched street name
        if (match && match.length > 1) {
            return match[match.length - 1];
        } else {
            return null; // No match found
        }
    }

    static getCxpAddressString(address?: CXPAddress ): string {
        if (!address) {
            return '';
        }
        return `${address.addressLine2 ? address.addressLine2 + ', ' : ''}${address.addressLine1}, ${address.city}, ${address.province ?? 'ON'}, ${address.postalCode}`;
    }

    static getAddressStringFromDetails(address: Address, noStreetNo: boolean, lang: 'en' | 'fr' = 'en'): string {
        if (!address || !address.postalCode) {
            return '';
        }

        let addressLine = '';
        if (!noStreetNo) {
            addressLine = `${address.address2 ? address.address2 + ', ' : ''}${address.address1}, ${address.municipality}, ${address.province ?? 'ON'}, ${address.postalCode}`;
        } else {
            const lot = address.lotType ? LotTypeMap[lang.toUpperCase()][address.lotType] + ' ' + address.lotNumber + ', ' : '';
            const pid = address.identifier ? PropertIdentifierMap[lang.toUpperCase()][address.identifier] + ' ' + address.identifierNumber + ', ' : '';
            const township = address.township ? address.township + ', ' : '';
            const unit = address.unitType? address.unitType + ' ' + address.suiteNumber + ', ' : address.suiteNumber ? 'Unit ' + address.suiteNumber + ', ': '';
            const city = `${address.municipality}, ${address.province || 'ON'}, ${address.postalCode}`;
            addressLine = lot + pid + township + unit + city;
        }
        return addressLine;
    }

    static getUnite = (address: Address, mask = false): string => {
        let unite = '';
        if (address.suiteNumber && address.unitType) {
            unite = address.unitType;
            unite += mask ? DataUtils.maskString(address.suiteNumber, 0) : address.suiteNumber + ' - ';
        }
        return unite;
    }

    static getAddressLine = (address: Address, mask = false): string => {
        if (address.isNoStreetNoAddress) {
            return '';
        }
        const suffix = address.streetNumberSuffix?.trim() ? mask? DataUtils.maskString(address.streetNumberSuffix.trim(), 0) : '' : '';
        let name = (address.streetName + ' ' + (address.streetType ?? '') + ' ' + (address.streetDirection ?? ''));
        name = mask? DataUtils.maskString(name.length > 10 ? name.substring(0, 10) : name, 0) : name;
        return (address.streetNumber + ' ' + suffix + ' ' + name + ', ')
            .replace(/\s+/g, ' ')
            .replace(/\s+,/g, ',');
    }

    static getCityAndPost = (address: Address, mask = false): string => {
        const city = mask? DataUtils.maskString(address.municipality, 0) : address.municipality;
        const postCode = mask? DataUtils.maskString(address.postalCode, 0, 3) : address.postalCode;
        return city + ', ' + address.province + ', ' + postCode;
    }

    static getRouteType = (address: Address, mask = false): string => {
        let rt = ''
        if (address.routeType && address.routeType) {
            rt = address.routeType + ' ' + address.routeNumber + ', ';
        }
        return rt;
    }

    static getPoBox = (address: Address, mask = false): string => {
        if (!address.poBoxNumber) return '';
        return address.poBoxNumber + ', ';
    }

    static getDeliveryInstallation = (address: Address, mask = false): string => {
        let di = '';
        if (address.deliveryInstallationType && address.deliveryInstallationName && address.deliveryInstallationArea) {
            di = address.deliveryInstallationType + ' ' + address.deliveryInstallationName + ' ' + address.deliveryInstallationArea + ', ';
        }
        return di;
    }

    static getRouteAndDeliveryInstallation = (address: Address, mask = false): string => {
        return Utils.getRouteType(address, mask) + Utils.getDeliveryInstallation(address, mask);
    }

    static getAddress = (address: Address, mask=false): string =>{
        if (address.isNoStreetNoAddress) {
            return Utils.getUnite(address, mask) + Utils.getPoBox(address, mask) + Utils.getDeliveryInstallation(address, mask)
                + Utils.getCityAndPost(address, mask);
        } else {
            return Utils.getUnite(address, mask) + Utils.getAddressLine(address, mask)
                + Utils.getRouteAndDeliveryInstallation(address, mask) + Utils.getCityAndPost(address, mask);
        }
    }

    static upperCase = (val: string): string => {
        return val?.toUpperCase();
    }
}
